import { Inject, Injectable, Optional } from '@angular/core';

import { noop } from 'src/app/utils/functions/noop';
import { OWCC_APP_NAME } from 'src/build-env';
import { Environment } from 'src/configs/environment/environment.interface';
import { environmentRef } from 'src/configs/environment/environment.token';

import { DevModeService } from '../../dev/dev-mode/dev-mode.service';

import { ConsoleFn } from './console-fn.type';
import { LogMethod } from './log-method.enum';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly tag: string = OWCC_APP_NAME;
  private readonly tagColor: string = 'deepskyblue';

  public constructor(
    @Optional() private readonly devModeService?: DevModeService,
    @Optional() @Inject(environmentRef) private readonly environment?: Environment
  ) {}

  public get log(): ConsoleFn {
    return this.getConsoleFn(LogMethod.LogMessage);
  }

  public get info(): ConsoleFn {
    return this.getConsoleFn(LogMethod.InfoMessage);
  }

  public get warn(): ConsoleFn {
    return this.getConsoleFn(LogMethod.WarnMessage);
  }

  public get error(): ConsoleFn {
    return this.getConsoleFn(LogMethod.ErrorMessage);
  }

  public get loudLog(): ConsoleFn {
    return this.getConsoleFn(LogMethod.LogMessage, true);
  }

  public get loudInfo(): ConsoleFn {
    return this.getConsoleFn(LogMethod.InfoMessage, true);
  }

  public get loudWarn(): ConsoleFn {
    return this.getConsoleFn(LogMethod.WarnMessage, true);
  }

  /**
   * Determine if logging should be silent
   */
  private get silent(): boolean {
    // if the logger service has been instantiated without dependency injection,
    // dependencies will not be available and therefor the output of this logger
    // instance will never be silenced
    if (!this.environment || !this.devModeService) {
      return false;
    }
    // if devMode is enabled, logger output is not silenced
    if (this.devModeService?.isDevMode) {
      return false;
    }
    // if environment is test, logger output is not silenced
    if (this.environment?.test) {
      return false;
    }
    // if envirenment is production, silence output
    return !!this.environment?.production;
  }

  /**
   * Prepend some fancy prefix to output
   */
  private get params(): string[] {
    // in test environment, do not add a colored prefix to output
    if (this.environment?.test) {
      return [];
    }

    // add a colored prefix to output
    return [`%c[${this.tag}]`, `color: ${this.tagColor}`];
  }

  /**
   * Creates a log function bound to respective console context
   *
   * @param method - the type of the log method
   * @param loud - whether the output should be loud regardless of silent
   */
  private getConsoleFn(method: LogMethod, loud = false): ConsoleFn {
    if (loud || !this.silent || [LogMethod.ErrorMessage, LogMethod.WarnMessage].includes(method)) {
      return console[method].bind(console, ...this.params) as ConsoleFn; // eslint-disable-line no-console
    }

    return noop;
  }
}
